// Single source of truth for inventory locations.
//
// The 2026-05 CK split turned the one central kitchen into two physical
// kitchens — CK_RH (Richmond Hill) and CK_NY (North York). Two layers:
//
//   - OPERATIONAL (当日现货 grid, slide-over, activity log): show the real
//     kitchen. Staff need to know WHERE stock physically is.
//   - REPORTING (统计报表, 报废分析): collapse both kitchens into one CK
//     figure — Regina reports kitchen output combined.
//
// Before this module the location list was copy-pasted in 4+ files as
// ['RH','DT','NY','CK']. Import from here instead so the split + the
// report-collapse rule live in exactly one place.

export type Location = 'RH' | 'DT' | 'NY' | 'CK_RH' | 'CK_NY';
export type ReportLocation = 'RH' | 'DT' | 'NY' | 'CK';

// Operational location set — grid columns, slide-over balances, filters,
// transfer targets. Order is NY-DT-RH (Regina, 2026-06-03): the display order
// staff scan, kitchens last.
export const LOCATIONS: Location[] = ['NY', 'DT', 'RH', 'CK_RH', 'CK_NY'];
// Storefronts sell; kitchens produce. Production is kitchen-only.
export const STOREFRONTS: Location[] = ['NY', 'DT', 'RH'];
export const KITCHENS: Location[] = ['CK_RH', 'CK_NY'];
// Reporting location set — 统计 / 报废 columns (kitchens summed to one CK).
export const REPORT_LOCATIONS: ReportLocation[] = ['NY', 'DT', 'RH', 'CK'];

const LABELS: Record<Location, string> = {
  RH: 'RH', DT: 'DT', NY: 'NY', CK_RH: 'CK(RH)', CK_NY: 'CK(NY)'
};

/** Display label for a location. Unknown values pass through unchanged. */
export function locationLabel(loc: string): string {
  return LABELS[loc as Location] ?? loc;
}

export function isKitchen(loc: string): boolean {
  return loc === 'CK_RH' || loc === 'CK_NY';
}

export function isStorefront(loc: string): boolean {
  return loc === 'RH' || loc === 'DT' || loc === 'NY';
}

/**
 * Collapse an operational location to its reporting bucket: both kitchens
 * map to 'CK', storefronts pass through. Used by 统计报表 + 报废分析 so the
 * two kitchens are summed into one CK figure.
 */
export function reportLocation(loc: string): ReportLocation {
  return isKitchen(loc) ? 'CK' : (loc as ReportLocation);
}
